/**
 * 网格管理器。
 * 职责：
 * 1. 监听 scene 及其子树上的 childadded / childremoved 事件；
 * 2. 自动维护草图对象的分类型集合；
 * 3. 统一提供草图对象收集结果的访问入口；
 * 4. 提供基础网格构建能力。
 */
import * as THREE from 'three/webgpu';
import type {Viewer} from '../Viewer';
import { SceneObjectMembershipTracker } from '../scene';
import {FeatureMeshMaterialFactory} from '../../material';
import {
    AbstractSketchEdgeObject,
    isAbstractSketchEdgeObject,
    isSketchArcObject,
    isSketchCircleObject,
    isSketchCollectionObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchPointObject,
    isSketchSlotObject,
    isSketchSplineObject,
    SketchArcObject,
    SketchCircleObject,
    SketchCollectionObject,
    SketchEllipseObject,
    SketchLineObject,
    SketchPointObject,
    SketchSlotObject,
    SketchSplineObject,
} from '../../mesh';
import {FeaturePreviewMesh} from '../../mesh/feature/FeaturePreviewMesh';
import {
    defaultFeatureMeshObjectFactoryRegistry,
    type FeatureMeshBuildContext,
    type FeatureTopologyProxyMountMode,
    type FeatureTopologySelectionMode,
    FeatureResultMesh,
    isFeatureResultMesh,
} from '../../mesh/feature';
import {isSketchEntityReferencePreview} from '../../reference/sketch-entity-reference/command/preview/SketchEntityReferencePreviewMarker';
import { RevolveCutKeepBodyPreviewManager } from './RevolveCutKeepBodyPreviewManager';
import { assertRenderableMeshData } from '../../utils/mesh/assertRenderableMeshData';
import { GeometryType } from '../../geometry/GeometryType';
import type { ModelBodySceneRegistry } from '../../model-body/ModelBodySceneRegistry';
import { resolveModelBodySceneRegistry } from '../../model-body/model-body-scene-registry-runtime';
import { resolveModelBodyPreviewReplacementRevisionIds } from '../../model-body/model-body-preview-presentation';


/**
 * 判断预览体是否需要同步展示轮廓。
 *
 * 设计说明：
 * 1. 特征操作过程中的预览体必须具备轮廓线，不能再按特征类型分叉；
 * 2. `FeaturePreviewMesh` 会优先消费权威拓扑，缺少拓扑时回退到当前网格轻量轮廓；
 * 3. 这里仅校验基础渲染数据，避免圆角/倒角重新触发高成本 Puppy CAD 内核 拓扑枚举。
 *
 * @param meshData 预览网格数据。
 * @returns 是否同步预览轮廓。
 */
function shouldSyncPreviewLines(meshData: MeshData): boolean {
    return meshData.positions.length > 0 && meshData.normals.length > 0;
}



/**
 * 网格管理器类。
 * 网格、注册表及资源立即更新，普通画面请求统一在宿主帧末提交，避免绘制替换中间态。
 */
export class MeshManager {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;
    /** 场景引用。 */
    private readonly scene: THREE.Scene;
    /** 场景统一实体生命周期注册表。 */
    private readonly modelBodySceneRegistry: ModelBodySceneRegistry;

    /** 草图与导入候选共用的物理场景成员跟踪器。 */
    private readonly sceneMembership: SceneObjectMembershipTracker;
    /** 本管理器自己的草图成员订阅，独立于其它消费者。 */
    private readonly unsubscribeSceneMembership: () => void;

    /** 草图对象总集合。 */
    private readonly sketchObjectSet: Set<ManagedSketchSceneObject> = new Set();

    /** 草图边对象集合。 */
    private readonly sketchEdgeObjectSet: Set<AbstractSketchEdgeObject> = new Set();

    /** 草图点对象集合。 */
    private readonly sketchPointObjectSet: Set<SketchPointObject> = new Set();

    /** 草图线对象集合。 */
    private readonly sketchLineObjectSet: Set<SketchLineObject> = new Set();

    /** 草图圆弧对象集合。 */
    private readonly sketchArcObjectSet: Set<SketchArcObject> = new Set();

    /** 草图圆对象集合。 */
    private readonly sketchCircleObjectSet: Set<SketchCircleObject> = new Set();

    /** 草图椭圆对象集合。 */
    private readonly sketchEllipseObjectSet: Set<SketchEllipseObject> = new Set();

    /** 草图样条曲线对象集合。 */
    private readonly sketchSplineObjectSet: Set<SketchSplineObject> = new Set();

    /** 草图轮廓集合对象集合。 */
    private readonly sketchCollectionObjectSet: Set<SketchCollectionObject> = new Set();

    /** 草图槽口对象集合。 */
    private readonly sketchSlotObjectSet: Set<SketchSlotObject> = new Set();
    /** 正式结果网格映射。 */
    private readonly resultMeshMap: Map<string, THREE.Mesh> = new Map();
    /** 项目打开阶段从 Worker 原始 BRep 物化、独立于 Feature 结果清理的 replay 基线网格。 */
    private readonly featureReplayBaselineMeshMap: Map<string, THREE.Mesh> = new Map();
    /** 被设计树隐藏的特征 ID 集合。 */
    private readonly hiddenFeatureIdSet: Set<string> = new Set();
    /** 预览网格映射。 */
    private readonly previewMeshMap: Map<string, THREE.Mesh> = new Map();
    /** 旋转切除保留实体高亮管理器。 */
    private readonly revolveCutKeepBodyPreviewManager: RevolveCutKeepBodyPreviewManager;
    /** 预览网格键到统一实体注册表预览请求键的映射。 */
    private readonly previewReplacementKeysByMeshKey: Map<string, string[]> = new Map();
    /**
     * 默认浏览态结果体拓扑代理挂载模式。
     *
     * 设计说明：
     * 1. 当前选择系统的实体点、线、面 hover/click 事件依赖
     *    FeaturePoint / FeatureLine / FeatureFace 进入场景树后参与候选收集；
     * 2. 因此默认浏览态必须保持 `all`，不能为了降低渲染负担改成 `none`，
     *    否则实体点、线、面会失去鼠标经过和点击响应；
     * 3. 圆角后的性能问题应从网格细分默认值、统计口径和后续非渲染代理拾取架构收口；
     * 4. 若未来要取消常驻代理，必须先完成“主结果体 raycast + topology snapshot 语义对象”的底层拾取重构。
     */
    private readonly defaultInteractionTopologyProxyMode: FeatureTopologyProxyMountMode = 'all';
    /** 当前命令态声明的拓扑选择模式。 */
    private featureTopologySelectionMode: FeatureTopologySelectionMode = 'none';

    /**
     * 构造函数。
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
        this.scene = viewer.scene;
        this.modelBodySceneRegistry = resolveModelBodySceneRegistry(this.scene);
        this.revolveCutKeepBodyPreviewManager = new RevolveCutKeepBodyPreviewManager(this.scene);

        // 统一成员来源回放已有对象；后续增删只分类变化成员，不重复发现整棵场景。
        this.sceneMembership = new SceneObjectMembershipTracker(this.scene);
        this.unsubscribeSceneMembership = this.sceneMembership.subscribe({
            onObjectAdded: object => this.trackSingleSketchObject(object, 'add'),
            onObjectRemoved: object => this.trackSingleSketchObject(object, 'remove'),
        });
    }

    /**
     * 获取草图对象总集合。
     * @returns 草图对象只读集合。
     */
    public getSketchObjects(): ReadonlySet<ManagedSketchSceneObject> {
        return this.filterPreviewObjects(this.sketchObjectSet);
    }

    /**
     * 获取草图边对象集合。
     * @returns 草图边对象只读集合。
     */
    public getSketchEdgeObjects(): ReadonlySet<AbstractSketchEdgeObject> {
        return this.filterPreviewObjects(this.sketchEdgeObjectSet);
    }

    /**
     * 获取草图点对象集合。
     * @returns 草图点对象只读集合。
     */
    public getSketchPointObjects(): ReadonlySet<SketchPointObject> {
        return this.filterPreviewObjects(this.sketchPointObjectSet);
    }

    /**
     * 获取草图线对象集合。
     * @returns 草图线对象只读集合。
     */
    public getSketchLineObjects(): ReadonlySet<SketchLineObject> {
        return this.filterPreviewObjects(this.sketchLineObjectSet);
    }

    /**
     * 获取草图圆弧对象集合。
     * @returns 草图圆弧对象只读集合。
     */
    public getSketchArcObjects(): ReadonlySet<SketchArcObject> {
        return this.filterPreviewObjects(this.sketchArcObjectSet);
    }

    /**
     * 获取草图圆对象集合。
     * @returns 草图圆对象只读集合。
     */
    public getSketchCircleObjects(): ReadonlySet<SketchCircleObject> {
        return this.filterPreviewObjects(this.sketchCircleObjectSet);
    }

    /**
     * 获取草图椭圆对象集合。
     * @returns 草图椭圆对象只读集合。
     */
    public getSketchEllipseObjects(): ReadonlySet<SketchEllipseObject> {
        return this.filterPreviewObjects(this.sketchEllipseObjectSet);
    }

    /**
     * 获取草图样条曲线对象集合。
     * @returns 草图样条曲线对象只读集合。
     */
    public getSketchSplineObjects(): ReadonlySet<SketchSplineObject> {
        return this.filterPreviewObjects(this.sketchSplineObjectSet);
    }

    /**
     * 过滤掉临时挂进场景树的 Convert Entities preview 对象。
     * 说明：
     * 1. MeshManager 暴露的是“正式草图对象”查询视图；
     * 2. preview 对象虽然会进入场景树参与渲染，但不能重新进入依赖这些 getter 的统一交互链路；
     * 3. 与 SketchGroup 一样在 getter 层收口，避免各使用方散落重复过滤。
     *
     * @param objects 原始对象集合。
     * @returns 已排除 preview 的只读集合快照。
     */
    private filterPreviewObjects<TObject extends THREE.Object3D>(
        objects: ReadonlySet<TObject>,
    ): ReadonlySet<TObject> {
        const filteredObjects = new Set<TObject>();
        objects.forEach((object) => {
            if (isSketchEntityReferencePreview(object)) {
                return;
            }
            filteredObjects.add(object);
        });
        return filteredObjects;
    }

    /**
     * 获取草图轮廓集合对象集合。
     * @returns 草图轮廓集合对象只读集合。
     */
    public getSketchCollectionObjects(): ReadonlySet<SketchCollectionObject> {
        return this.sketchCollectionObjectSet;
    }

    /**
     * 获取草图槽口对象集合。
     * @returns 草图槽口对象只读集合。
     */
    public getSketchSlotObjects(): ReadonlySet<SketchSlotObject> {
        return this.sketchSlotObjectSet;
    }

    public dispose(): void {
        this.revolveCutKeepBodyPreviewManager.dispose();
        this.clearFeatureMeshes();
        this.clearFeatureReplayBaselineMeshes();
        this.hiddenFeatureIdSet.clear();
        this.unsubscribeSceneMembership();
        this.sceneMembership.dispose();
        this.clearTrackedSketchObjectSets();
    }

    /**
     * 设置旋转切除保留实体片段高亮网格。
     *
     * @param fragmentMeshes 片段网格。
     */
    public setRevolveCutKeepBodyPreviewFragments(fragmentMeshes: RevolveCutPreviewFragmentMesh[]): void {
        this.revolveCutKeepBodyPreviewManager.setFragments(fragmentMeshes);
        this.viewer.requestRender();
    }

    /**
     * 更新旋转切除保留实体高亮选择。
     *
     * @param selection 草稿选择。
     */
    public updateRevolveCutKeepBodyPreviewSelection(selection: Record<string, string[]>): void {
        this.revolveCutKeepBodyPreviewManager.updateSelection(selection);
        this.viewer.requestRender();
    }

    /**
     * 清空旋转切除保留实体高亮。
     */
    public clearRevolveCutKeepBodyPreview(): void {
        this.revolveCutKeepBodyPreviewManager.clear();
        this.viewer.requestRender();
    }

    /**
     * 添加或替换正式结果网格。
     * @param meshData 网格数据。
     * @returns 场景网格对象。
     */
    public async upsertResultMesh(meshData: MeshData, context?: FeatureMeshBuildContext): Promise<THREE.Mesh> {
        return this.upsertFeatureMesh(meshData, false, context);
    }

    /**
     * 添加或替换项目打开阶段物化的原始导入 replay 基线网格。
     *
     * 该网格不是 Feature 命令结果，`clearFeatureMeshes()` 不得清理它；其生命周期由
     * `clearFeatureReplayBaselineMeshes()` 或 MeshManager.dispose() 独立收口。
     *
     * @param meshData Worker 原始导入 BRep 生成的网格数据。
     * @returns 场景中的基线网格。
     */
    public async upsertFeatureReplayBaselineMesh(meshData: MeshData): Promise<THREE.Mesh> {
        return this.upsertFeatureMesh(meshData, false, undefined, true);
    }

    /**
     * 设置指定预览网格。
     * @param previewKey 预览键。
     * @param meshData 网格数据；传 null 表示移除。
     * @returns 预览网格对象；移除时返回 null。
     */
    public async setPreviewMesh(
        previewKey: string,
        meshData: MeshData | null,
        context?: FeatureMeshBuildContext,
    ): Promise<THREE.Mesh | null> {
        if (!meshData) {
            this.removePreviewMesh(previewKey);
            return null;
        }
        try {
            const previewMesh = await this.upsertFeatureMesh(
                {
                    ...meshData,
                    id: previewKey,
                },
                true,
                context,
            );
            this.syncPreviewSourceVisibility(previewKey, meshData);
            return previewMesh;
        } catch (error) {
            this.clearPreviewSourceVisibility(previewKey);
            throw error;
        }
    }

    /**
     * 移除正式结果网格。
     * @param meshId 网格 ID。
     */
    public removeResultMesh(meshId: string): void {
        const mesh = this.resultMeshMap.get(meshId);
        if (!mesh) {
            return;
        }
        this.modelBodySceneRegistry.unregisterRepresentation(meshId);
        this.disposeFeatureMesh(mesh);
        this.resultMeshMap.delete(meshId);
    }

    /**
     * 移除预览网格。
     * @param previewKey 预览键。
     */
    public removePreviewMesh(previewKey: string): void {
        this.clearPreviewSourceVisibility(previewKey);
        const mesh = this.previewMeshMap.get(previewKey);
        if (!mesh) {
            return;
        }
        this.disposeFeatureMesh(mesh);
        this.previewMeshMap.delete(previewKey);
    }

    /**
     * 获取正式结果网格。
     * @param meshId 网格 ID。
     * @returns 网格对象。
     */
    public getResultMesh(meshId: string): THREE.Mesh | null {
        return this.resultMeshMap.get(meshId) ?? null;
    }

    /**
     * 判断正式结果网格是否仍然真实挂载在当前 scene 树上。
     *
     * 设计说明：
     * 1. `resultMeshMap` 是 MeshManager 的索引，不等价于渲染场景的最终事实；
     * 2. 回退控制棒、重建或外部同步过程中，旧对象可能已从 scene 移除但索引尚未被替换；
     * 3. 增量回灌必须以 scene 挂载状态作为渲染权威，避免把已脱离场景的旧 mesh 误判为可见实体。
     *
     * @param meshId 正式结果网格 ID。
     * @returns 结果网格是否仍在当前 scene 树中。
     */
    public hasResultMeshInScene(meshId: string): boolean {
        const mesh = this.resultMeshMap.get(meshId);
        if (!mesh) {
            return false;
        }
        return this.isObjectAttachedToScene(mesh);
    }

    /**
     * 判断对象是否挂载在当前 MeshManager 管理的 scene 树中。
     *
     * @param object 待检查的 three.js 对象。
     * @returns 对象是否可以从当前 scene 追溯到根。
     */
    private isObjectAttachedToScene(object: THREE.Object3D): boolean {
        let currentObject: THREE.Object3D | null = object;
        while (currentObject) {
            if (currentObject === this.scene) {
                return true;
            }
            currentObject = currentObject.parent;
        }
        return false;
    }

    /**
     * 读取当前被隐藏的特征 ID。
     *
     * @returns 特征 ID 列表。
     */
    public getHiddenFeatureIds(): string[] {
        return Array.from(this.hiddenFeatureIdSet);
    }

    /**
     * 判断指定特征结果体是否被隐藏。
     *
     * @param featureId 特征 ID。
     * @returns 是否隐藏。
     */
    public isFeatureResultHidden(featureId: string): boolean {
        return this.hiddenFeatureIdSet.has(featureId);
    }

    /**
     * 设置结果体拓扑代理模式。
     *
     * 设计说明：
     * 1. 这里记录的是“命令态希望限制到什么选择范围”，不是默认浏览态的完整交互语义；
     * 2. 默认浏览态仍应暴露 development 分支已有的面/边/点交互代理，不能被 `object` 模式关掉；
     * 3. 因此真正广播给结果体的挂载模式，要由“默认浏览态挂载模式 + 命令态覆盖模式”共同决议。
     *
     * @param mode 目标代理模式。
     */
    public setFeatureTopologySelectionMode(mode: FeatureTopologySelectionMode): void {
        if (this.featureTopologySelectionMode === mode) {
            return;
        }
        this.featureTopologySelectionMode = mode;
        this.syncResultMeshTopologyProxyMode();
    }

    /**
     * 读取当前命令态声明的结果体拓扑选择模式。
     *
     * @returns 当前命令态选择模式；`none` 表示回退到默认浏览态代理挂载。
     */
    public getFeatureTopologySelectionMode(): FeatureTopologySelectionMode {
        return this.featureTopologySelectionMode;
    }

    /**
     * 设置由指定特征创建的正式结果体可见性。
     *
     * 设计说明：
     * 1. 特征编辑时需要临时隐藏旧正式结果体，避免与预览体叠加；
     * 2. 这里按 `metadata.featureId` 统一筛选，不让上层直接操作 THREE.Mesh.visible；
     * 3. 仅作用于正式结果体，不影响预览体与草图对象。
     *
     * @param featureId 特征 ID。
     * @param visible 是否可见。
     */
    public setResultMeshVisibilityByFeatureId(featureId: string, visible: boolean): void {
        if (visible) {
            this.hiddenFeatureIdSet.delete(featureId);
        } else {
            this.hiddenFeatureIdSet.add(featureId);
        }
        let hasVisibilityChanged = false;
        this.resultMeshMap.forEach((mesh) => {
            if (!isFeatureResultMesh(mesh)) {
                return;
            }
            const metadata = mesh.getFeatureMetadata();
            if (metadata.featureId !== featureId) {
                return;
            }
            hasVisibilityChanged = hasVisibilityChanged || mesh.visible !== visible;
            mesh.visible = visible;
        });
        if (hasVisibilityChanged) {
            this.viewer.notifySectionableModelContentChanged();
        }
        this.viewer.requestRender();
    }

    /**
     * 清理全部特征网格。
     */
    public clearFeatureMeshes(options: {
        /** 是否保留当前特征预览体。 */
        preservePreviewMeshes?: boolean;
    } = {}): void {
        Array.from(this.resultMeshMap.keys()).forEach((meshId) => {
            this.removeResultMesh(meshId);
        });
        if (options.preservePreviewMeshes === true) {
            return;
        }
        Array.from(this.previewMeshMap.keys()).forEach((previewKey) => {
            this.removePreviewMesh(previewKey);
        });
    }

    /**
     * 清理项目打开阶段物化的全部原始导入 replay 基线网格。
     *
     * 新文档接管 Viewer 或 Viewer 释放时必须调用，避免上一零件的隐藏基线继续占用场景与显存。
     */
    public clearFeatureReplayBaselineMeshes(): void {
        this.featureReplayBaselineMeshMap.forEach((mesh, meshId) => {
            this.modelBodySceneRegistry.unregisterRepresentation(
                this.resolveFeatureReplayBaselineRepresentationId(meshId),
            );
            this.disposeFeatureMesh(mesh);
        });
        this.featureReplayBaselineMeshMap.clear();
    }

    /**
     * 暂停显示为 replay 补建的导入基线，但保留其网格与注册身份供后续重放恢复。
     *
     * 该入口只处理本管理器独立物化的基线表示，不会误伤离线最终场景中仍合法可见的
     * 未修改导入实体，即使两者使用同一个 model-body revision。
     */
    public deactivateFeatureReplayBaselineMeshes(): void {
        const representationIds = new Set(
            [...this.featureReplayBaselineMeshMap.keys()]
                .map((meshId) => this.resolveFeatureReplayBaselineRepresentationId(meshId)),
        );
        this.modelBodySceneRegistry.deactivateRepresentations(representationIds);
    }

    /** @returns 可复用的场景成员来源；调用方只拥有订阅，不拥有跟踪器。 */
    public getSceneObjectMembershipSource(): SceneObjectMembershipSource {
        return this.sceneMembership;
    }

    /**
     * 按运行时 UUID 从共享场景成员索引定位对象。
     *
     * @param objectUuid Three.js 对象 UUID。
     * @returns 当前仍挂载在 Viewer 场景中的对象；未命中时返回 null。
     */
    public resolveSceneObjectByUuid(objectUuid: string): THREE.Object3D | null {
        return this.sceneMembership.resolveObjectByUuid(objectUuid);
    }

    /**
     * 跟踪单个草图对象。
     * @param object 目标对象。
     * @param action 监听动作。
     */
    private trackSingleSketchObject(object: THREE.Object3D, action: SceneTreeListenAction): void {
        if (isSketchPointObject(object)) {
            this.updateSetByAction(this.sketchPointObjectSet, object, action);
            this.updateSetByAction(this.sketchObjectSet, object, action);
            return;
        }

        if (isSketchSlotObject(object)) {
            this.updateSetByAction(this.sketchSlotObjectSet, object, action);
            this.updateSetByAction(this.sketchObjectSet, object, action);
            return;
        }

        if (isSketchCollectionObject(object)) {
            this.updateSetByAction(this.sketchCollectionObjectSet, object, action);
            this.updateSetByAction(this.sketchObjectSet, object, action);
            return;
        }

        if (isAbstractSketchEdgeObject(object)) {
            this.updateSetByAction(this.sketchEdgeObjectSet, object, action);
            this.updateSetByAction(this.sketchObjectSet, object, action);

            // 进一步细分具体边类型集合。
            if (isSketchLineObject(object)) {
                this.updateSetByAction(this.sketchLineObjectSet, object, action);
            }
            if (isSketchArcObject(object)) {
                this.updateSetByAction(this.sketchArcObjectSet, object, action);
            }
            if (isSketchCircleObject(object)) {
                this.updateSetByAction(this.sketchCircleObjectSet, object, action);
            }
            if (isSketchEllipseObject(object)) {
                this.updateSetByAction(this.sketchEllipseObjectSet, object, action);
            }
            if (isSketchSplineObject(object)) {
                this.updateSetByAction(this.sketchSplineObjectSet, object, action);
            }
        }
    }

    /**
     * 根据动作更新集合。
     * @param targetSet 目标集合。
     * @param object 目标对象。
     * @param action 监听动作。
     */
    private updateSetByAction<TObject extends THREE.Object3D>(
        targetSet: Set<TObject>,
        object: TObject,
        action: SceneTreeListenAction,
    ): void {
        if (action === 'add') {
            targetSet.add(object);
            return;
        }

        targetSet.delete(object);
    }

    /**
     * 清空草图对象跟踪集合。
     */
    private clearTrackedSketchObjectSets(): void {
        this.sketchObjectSet.clear();
        this.sketchEdgeObjectSet.clear();
        this.sketchPointObjectSet.clear();
        this.sketchLineObjectSet.clear();
        this.sketchArcObjectSet.clear();
        this.sketchCircleObjectSet.clear();
        this.sketchEllipseObjectSet.clear();
        this.sketchSplineObjectSet.clear();
        this.sketchCollectionObjectSet.clear();
        this.sketchSlotObjectSet.clear();
    }

    /**
     * 创建或替换特征网格。
     * @param meshData 网格数据。
     * @param isPreview 是否为预览网格。
     * @param context 特征网格构建上下文。
     * @param isFeatureReplayBaseline 是否为独立生命周期的原始导入 replay 基线。
     * @returns 场景网格对象。
     */
    private async upsertFeatureMesh(
        meshData: MeshData,
        isPreview: boolean,
        context?: FeatureMeshBuildContext,
        isFeatureReplayBaseline: boolean = false,
    ): Promise<THREE.Mesh> {
        if (!isPreview) {
            assertRenderableMeshData(meshData, {
                phase: 'MeshManager 正式结果网格回灌',
                objectId: meshData.id,
            });
        }

        const meshMap = isPreview
            ? this.previewMeshMap
            : isFeatureReplayBaseline
                ? this.featureReplayBaselineMeshMap
                : this.resultMeshMap;
        const previousMesh = meshMap.get(meshData.id);
        if (previousMesh) {
            if (!isPreview) {
                this.modelBodySceneRegistry.unregisterRepresentation(
                    isFeatureReplayBaseline
                        ? this.resolveFeatureReplayBaselineRepresentationId(meshData.id)
                        : meshData.id,
                );
            }
            this.disposeFeatureMesh(previousMesh);
            meshMap.delete(meshData.id);
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(meshData.positions, 3));
        geometry.setAttribute('normal', new THREE.Float32BufferAttribute(meshData.normals, 3));
        if (meshData.indices) {
            geometry.setIndex(new THREE.BufferAttribute(meshData.indices, 1));
        }
        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();

        const material = isPreview
            ? FeatureMeshMaterialFactory.createPreviewMaterial()
            : FeatureMeshMaterialFactory.createResultMaterial({ scene: this.viewer.scene });

        const mesh = this.createFeatureMeshObject({
            meshData,
            geometry,
            material,
            isPreview,
            context,
        });
        mesh.name = meshData.id;
        const topologySnapshot = meshData.topologySnapshot ?? null;
        if (isPreview && mesh instanceof FeaturePreviewMesh && shouldSyncPreviewLines(meshData)) {
            mesh.syncPreviewLines(topologySnapshot);
        }
        if (!isPreview && isFeatureResultMesh(mesh)) {
            mesh.syncTopologyVisuals(topologySnapshot);
            mesh.setTopologySelectionMode(this.resolveEffectiveTopologyProxyMode());
            
            this.syncFeatureResultMeshDisplayMode(mesh);
            
            const metadata = mesh.getFeatureMetadata();
            const featureId = typeof metadata.featureId === 'string' ? metadata.featureId : null;
            if (featureId && this.hiddenFeatureIdSet.has(featureId)) {
                mesh.visible = false;
            }
        }
        if (isPreview) {
            // 预览体不属于文档正式结果，保持在普通场景层级，避免被剖面视图误裁剪。
            this.viewer.addObject(mesh);
        } else {
            this.viewer.addSectionableObject(mesh);
        }
        meshMap.set(meshData.id, mesh);
        if (!isPreview) {
            const metadata = meshData.metadata ?? {};
            const modelBodyId = typeof metadata.modelBodyId === 'string' && metadata.modelBodyId.trim()
                ? metadata.modelBodyId.trim()
                : meshData.id;
            const modelBodyRevisionId = typeof metadata.modelBodyRevisionId === 'string'
                && metadata.modelBodyRevisionId.trim()
                ? metadata.modelBodyRevisionId.trim()
                : meshData.id;
            this.modelBodySceneRegistry.registerRepresentation({
                representationId: isFeatureReplayBaseline
                    ? this.resolveFeatureReplayBaselineRepresentationId(meshData.id)
                    : meshData.id,
                modelBodyId,
                modelBodyRevisionId,
                aliasIds: Array.isArray(metadata.modelBodyAliasIds)
                    ? metadata.modelBodyAliasIds.filter((aliasId): aliasId is string => typeof aliasId === 'string')
                    : [],
                displayMesh: mesh,
                topologyOwner: isFeatureResultMesh(mesh) ? mesh : null,
                /**
                 * 原始导入基线只在 Worker 历史位置完成判定后才能显示。
                 * 这里先以未激活表示登记，避免打开含最终 Feature 快照的文档时出现双实体。
                 */
                initiallyActive: !isFeatureReplayBaseline,
            });
            if (!isFeatureReplayBaseline) {
                this.modelBodySceneRegistry.applyWorkerObject({
                    id: meshData.id,
                    type: GeometryType.SOLID,
                    metadata,
                } as GeometryObject);
            }
        }
        this.viewer.requestRender();
        return mesh;
    }

    /**
     * 生成原始导入 replay 基线在统一实体注册表中的独立表示 ID。
     *
     * @param meshId Worker 原始导入 BRep 修订 ID。
     * @returns 不与普通 Feature 结果注册键冲突的表示 ID。
     */
    private resolveFeatureReplayBaselineRepresentationId(meshId: string): string {
        return `feature-replay-baseline:${meshId}`;
    }

    //------ 模型显示模式 开始------
    /**
     * 将当前 Viewer 显示模式同步到单个正式特征结果体。
     *
     * 设计说明：
     * 1. 合并结果体、单独结果体、编辑重放结果体和删除重放结果体最终都会经过 `upsertFeatureMesh()`；
     * 2. 这里直接同步当前结果体，避免每次回灌都遍历整个 scene；
     * 3. 显示边和不着色材质都由渲染控制器统一处理，MeshManager 只负责通知单体结果体已更新。
     *
     * @param mesh 正式特征结果体。
     */
    private syncFeatureResultMeshDisplayMode(mesh: FeatureResultMesh): void {
        this.viewer.syncFeatureResultMeshModelDisplayMode(mesh);
    }
    

    /**
     * 解析当前所有正式结果体应采用的真实代理挂载模式。
     *
     * 设计说明：
     * 1. 当命令态未声明限制时，应回退到默认浏览态的完整代理挂载；
     * 2. 一旦命令态声明了 edge / face / vertex / face-edge，就由命令态覆盖默认挂载；
     * 3. 这样可以同时保证普通浏览交互正确，以及命令态仍能控制拾取规模。
     *
     * @returns 当前生效的代理挂载模式。
     */
    private resolveEffectiveTopologyProxyMode(): FeatureTopologyProxyMountMode {
        if (this.featureTopologySelectionMode === 'none') {
            return this.defaultInteractionTopologyProxyMode;
        }
        return this.featureTopologySelectionMode;
    }

    /**
     * 向所有正式结果体同步当前生效的拓扑代理挂载模式。
     */
    private syncResultMeshTopologyProxyMode(): void {
        const effectiveMode = this.resolveEffectiveTopologyProxyMode();
        [
            ...this.resultMeshMap.values(),
            ...this.featureReplayBaselineMeshMap.values(),
        ].forEach((mesh) => {
            if (!isFeatureResultMesh(mesh)) {
                return;
            }
            mesh.setTopologySelectionMode(effectiveMode);
        });
        this.viewer.requestRender();
    }

    /**
     * 同步指定预览键对应的源实体预览替换状态。
     *
     * @param previewKey 预览键。
     * @param meshData 预览网格数据。
     */
    private syncPreviewSourceVisibility(previewKey: string, meshData: MeshData): void {
        this.clearPreviewSourceVisibility(previewKey);
        const sourceRevisionIds = resolveModelBodyPreviewReplacementRevisionIds(meshData);
        if (sourceRevisionIds.length === 0) {
            return;
        }

        const replacementKeys = sourceRevisionIds.map((sourceRevisionId, index) => {
            const replacementKey = `${previewKey}:model-body:${index}`;
            this.modelBodySceneRegistry.beginPreviewReplacement(replacementKey, sourceRevisionId);
            return replacementKey;
        });
        this.previewReplacementKeysByMeshKey.set(previewKey, replacementKeys);
        this.viewer.requestRender();
    }

    /**
     * 清理指定预览键登记的源实体预览替换状态。
     *
     * @param previewKey 预览键。
     */
    private clearPreviewSourceVisibility(previewKey: string): void {
        const replacementKeys = this.previewReplacementKeysByMeshKey.get(previewKey);
        if (!replacementKeys) {
            return;
        }

        replacementKeys.forEach((replacementKey) => {
            this.modelBodySceneRegistry.endPreviewReplacement(replacementKey);
        });
        this.previewReplacementKeysByMeshKey.delete(previewKey);
        this.viewer.requestRender();
    }

    /**
     * 根据上下文创建特征结果体或预览体对象。
     *
     * 设计说明：
     * 1. Feature mesh 的创建优先交给专属工厂注册表，按 prototype 命中特征专属结果体与预览体对象；
     * 2. 专属对象与 prototype 关联后，编辑会话与设计树都可以从结果体直接回溯到特征真相；
     * 3. 当当前 mesh 不属于特征对象链时，仍使用通用网格对象承载非特征场景 mesh。
     *
     * @param input 输入参数。
     * @returns 三维网格对象。
     */
    private createFeatureMeshObject(input: {
        meshData: MeshData;
        geometry: THREE.BufferGeometry;
        material: THREE.Material | THREE.Material[];
        isPreview: boolean;
        context?: FeatureMeshBuildContext;
    }): THREE.Mesh {
        const featureId = typeof input.meshData.metadata?.featureId === 'string'
            ? input.meshData.metadata.featureId
            : null;
        const specializedMesh = defaultFeatureMeshObjectFactoryRegistry.create(input);
        if (specializedMesh) {
            return specializedMesh;
        }
        if (input.context?.prototype || featureId) {
            throw new Error(`未为特征结果体提供专属对象实现：${input.context?.prototype?.getFeatureKind?.() ?? featureId}`);
        }
        if (input.isPreview) {
            if (input.context?.prototype || input.context?.draftDefinition || featureId) {
                throw new Error(`未为特征预览体提供专属对象实现：${input.context?.prototype?.getFeatureKind?.() ?? featureId}`);
            }
            return new FeaturePreviewMesh({
                meshData: input.meshData,
                geometry: input.geometry,
                material: input.material,
            });
        }
        return new FeatureResultMesh({
            meshData: input.meshData,
            geometry: input.geometry,
            material: input.material,
        });
    }

    /**
     * 释放特征网格。
     * @param mesh 目标网格。
     */
    private disposeFeatureMesh(mesh: THREE.Mesh): void {
        this.viewer.entitySelectionManager?.removeObjectTreeFromSelection?.(mesh);
        const wasSectionable = !(mesh instanceof FeaturePreviewMesh);
        if (mesh.parent) {
            mesh.parent.remove(mesh);
        }
        if (wasSectionable) {
            // 必须先解除 stencil 代理对源 geometry 的引用，再释放正式结果体 GPU 资源。
            this.viewer.notifySectionableModelContentChanged();
        }
        if (mesh instanceof FeaturePreviewMesh) {
            mesh.disposePreviewResources();
        }
        if (isFeatureResultMesh(mesh)) {
            // 不着色模式会临时替换正式结果体材质，释放前必须恢复原材质槽。
            this.viewer.restoreFeatureResultMeshModelDisplayModeMaterial(mesh);
            mesh.clearTopologyVisuals();
        }
        mesh.geometry.dispose();
        if (Array.isArray(mesh.material)) {
            mesh.material.forEach((material) => material.dispose());
        } else {
            mesh.material.dispose();
        }
        this.viewer.requestRender();
    }

}
